import { Grid2, Stack, Typography } from '@mui/material';
import { camelCase } from 'change-case';
import { navigate } from 'gatsby';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import NullStateDiv from '@components/NullStateDiv';
import ProfileCard from '@components/ProfileCard';
import RoundedButton from '@components/RoundedButton';
import SkeletonLoader from '@components/SkeletonLoader';

import * as ROUTES from '@constants/routes';
import { MANAGE_ACCESS_MANAGEMENT } from '@constants/routes';

import { useListAccessManagementUsers } from '@hooks/api/access-permission';

import type { Profile } from '@models/session';

import { formatName } from '@utils/format';
import { displayUserRoles } from '@utils/role';

import { UserAccessLevel, type UserAccess } from 'generated/types';

const messages = defineMessages({
  invite: {
    id: 'accessManagement.inviteNewUser',
    defaultMessage: 'Invite New User',
  },
  fullAccess: {
    id: 'accessManagement.fullAccess',
    defaultMessage: 'Full Access',
  },
  noAccess: {
    id: 'accessManagement.noAccess',
    defaultMessage: 'No Access',
  },
  allergyIntolerance: {
    defaultMessage: 'Allergies',
    id: 'accessManagement.allergyIntolerance',
  },
  appointment: {
    defaultMessage: 'Appointments',
    id: 'accessManagement.appointment',
  },
  auth: {
    defaultMessage: 'Profile and Security',
    id: 'accessManagement.auth',
  },
  body: {
    defaultMessage: 'Body',
    id: 'accessManagement.body',
  },
  document: {
    defaultMessage: 'Records/Documents',
    id: 'accessManagement.document',
  },
  emergencyVisit: {
    defaultMessage: 'Emergency Visits',
    id: 'accessManagement.emergencyVisit',
  },
  fitness: {
    id: 'accessManagement.fitness',
    defaultMessage: 'Fitness',
  },
  genetic: {
    id: 'accessManagement.genetic',
    defaultMessage: 'Genetic Information',
  },
  hospitalization: {
    id: 'accessManagement.hospitalization',
    defaultMessage: 'Hospitalizations',
  },
  imagingTest: {
    id: 'accessManagement.imagingTest',
    defaultMessage: 'Imaging and Tests',
  },
  labReport: {
    id: 'accessManagement.labReport',
    defaultMessage: 'Lab Results',
  },
  medication: {
    id: 'accessManagement.medication',
    defaultMessage: 'Medications',
  },
  sleep: {
    id: 'accessManagement.sleep',
    defaultMessage: 'Sleep',
  },
  vitalSign: {
    id: 'accessManagement.vitalSign',
    defaultMessage: 'Vital Signs',
  },
  emergencyAccess: {
    id: 'accessManagement.emergencyAccess',
    defaultMessage: 'Emergency Access',
  },
  healthSummary: {
    id: 'accessManagement.healthSummary',
    defaultMessage: 'Health Summary',
  },
  legalDirective: {
    id: 'accessManagement.legalDirective',
    defaultMessage: 'Legal and Directives',
  },
  profileSecurity: {
    id: 'accessManagement.profileSecurity',
    defaultMessage: 'Profile and Security',
  },
  settings: {
    id: 'accessManagement.settings',
    defaultMessage: 'Settings',
  },
  timeline: {
    id: 'accessManagement.timeline',
    defaultMessage: 'Timeline',
  },
  condition: {
    defaultMessage: 'Conditions',
    id: 'accessManagement.condition',
  },
  consent: {
    defaultMessage: 'Consents',
    id: 'accessManagement.consent',
  },
  coverage: {
    defaultMessage: 'Insurance',
    id: 'accessManagement.coverage',
  },
  diagnosticReport: {
    defaultMessage: 'Lab Reports and Imaging Tests',
    id: 'accessManagement.diagnosticReport',
  },
  emrConnection: {
    defaultMessage: 'Hospital/Clinic Connections',
    id: 'accessManagement.emrConnection',
  },
  encounter: {
    defaultMessage: 'Emergency Visits and Hospitalizations',
    id: 'accessManagement.encounter',
  },
  familyMemberHistory: {
    defaultMessage: 'Family History',
    id: 'accessManagement.familyMemberHistory',
  },
  feedback: {
    defaultMessage: 'Submit Feedback',
    id: 'accessManagement.feedback',
  },
  immunization: {
    defaultMessage: 'Immunizations',
    id: 'accessManagement.immunization',
  },
  medicationStatement: {
    defaultMessage: 'Medications',
    id: 'accessManagement.medicationStatement',
  },
  notification: {
    defaultMessage: 'Notifications',
    id: 'accessManagement.notification',
  },
  observation: {
    defaultMessage: 'Vital Signs, Genetic Information, etc.',
    id: 'accessManagement.observation',
  },
  procedure: {
    defaultMessage: 'Surgeries',
    id: 'accessManagement.procedure',
  },
  record: {
    defaultMessage: 'Records/Documents',
    id: 'accessManagement.record',
  },
  relatedPerson: {
    defaultMessage: 'Emergency Contacts, Legal Counsel, POA',
    id: 'accessManagement.relatedPerson',
  },
  routine: {
    defaultMessage: 'Routines',
    id: 'accessManagement.routine',
  },
  smartDevice: {
    defaultMessage: 'Health Devices',
    id: 'accessManagement.smartDevice',
  },
  scopeTrailing: {
    defaultMessage: '+{count} more...',
    id: 'accessManagement.scopeTrailing',
  },
  noUsers: {
    defaultMessage:
      'There are no other users and/or patients in your account. To grant access to another user, begin by inviting a new user to your account.',
    id: 'accessManagement.noAccountUsers',
  },
  message: {
    defaultMessage:
      'This is where you can manage access of the following users to YOUR profile. To manage access on behalf of others, switch to view their profile and proceed to the access management screen.',
    id: 'accessManagement.message',
  },
});

interface InvitedUsersProps {
  ownerUser?: Profile;
}

function InvitedUsers({ ownerUser }: InvitedUsersProps) {
  const { formatMessage } = useIntl();

  const { data: accessUsers, loading } = useListAccessManagementUsers(
    ownerUser?.user_id
  );

  function formatAccessLevel({ scopes, level }: UserAccess) {
    if (level === UserAccessLevel.Full) {
      return formatMessage(messages.fullAccess);
    }
    if (level === UserAccessLevel.None) {
      return formatMessage(messages.noAccess);
    }
    const filteredScopes = [...scopes]
      .sort((a, b) => a.localeCompare(b))
      .slice(0, 2);
    const remainder = scopes.length - filteredScopes.length;
    return [
      ...filteredScopes.map((scope) =>
        formatMessage({ id: `accessManagement.${camelCase(scope)}` })
      ),
      ...(remainder > 0
        ? [formatMessage(messages.scopeTrailing, { count: remainder })]
        : []),
    ].join(', ');
  }

  const handleInvite = () => {
    navigate(ROUTES.MANAGE_ACCESS_MANAGEMENT_INVITE);
  };

  return (
    <Stack spacing={1}>
      <Typography my={2} variant="caption">
        {formatMessage(messages.message)}
      </Typography>
      <Grid2 container justifyContent="flex-end">
        <RoundedButton
          height="15px"
          iconName="plus"
          margin={0}
          textTransform="capitalize"
          onClick={handleInvite}
          width="200px"
        >
          {formatMessage(messages.invite)}
        </RoundedButton>
      </Grid2>

      <SkeletonLoader count={3} loading={loading} height={100}>
        {accessUsers.length ? (
          accessUsers
            .filter(({ user_id }) => user_id !== ownerUser?.user_id)
            .map((user) => {
              const roles = displayUserRoles(user, ownerUser?.account_id ?? '');
              const noAccess = user.access_level?.level
                ? user.access_level.level === UserAccessLevel.None
                : true;

              return (
                <ProfileCard
                  key={`access-management-${user.user_id}`}
                  disabled={noAccess}
                  loading={false}
                  name={formatName(user.first_name, user.last_name)}
                  profile_picture={user.profile_picture}
                  onClick={
                    user.user_id
                      ? () =>
                          navigate(`${MANAGE_ACCESS_MANAGEMENT}${user.user_id}`)
                      : undefined
                  }
                  subtitle={roles
                    .map((messageId) => formatMessage({ id: messageId }))
                    .join(', ')}
                >
                  <Typography color="text.secondary" variant="subtitle2">
                    {formatAccessLevel(
                      user.access_level ?? {
                        scopes: [],
                        level: UserAccessLevel.None,
                      }
                    )}
                  </Typography>
                </ProfileCard>
              );
            })
        ) : (
          <NullStateDiv>{formatMessage(messages.noUsers)}</NullStateDiv>
        )}
      </SkeletonLoader>
    </Stack>
  );
}

export default withErrorBoundary(InvitedUsers, {
  FallbackComponent: ErrorBoundaryFallback,
});
